import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Column } from "primereact/column";
import { DataTable, DataTablePageEvent } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";

import {
  getFundingCampaignSubmissions,
  FundingCampaignStatus,
  FundingCampaignSubmission,
} from "../../../services/fundingCampaign";

const STATUS_OPTIONS = [
  { label: "Submitted", value: "SUBMITTED" },
  { label: "Forwarded", value: "FORWARDED" },
  { label: "Offer Received", value: "OFFER_RECEIVED" },
  { label: "Application Ready", value: "APPLICATION_READY" },
];

const STATUS_LABEL: Record<FundingCampaignStatus, string> = {
  SUBMITTED: "Submitted",
  FORWARDED: "Forwarded",
  OFFER_RECEIVED: "Offer Received",
  APPLICATION_READY: "Application Ready",
};

type TagSeverity = "info" | "success" | "warning" | "danger" | null | undefined;

const STATUS_SEVERITY: Record<FundingCampaignStatus, TagSeverity> = {
  SUBMITTED: "info",
  FORWARDED: "warning",
  OFFER_RECEIVED: "danger",
  APPLICATION_READY: "success",
};

const WallboxFundingPage: React.FC = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<FundingCampaignSubmission[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(25);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<FundingCampaignStatus | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setFirst(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setFirst(0);
  }, [statusFilter]);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getFundingCampaignSubmissions({
        offset: first,
        limit: rows,
        q: debouncedSearch || undefined,
        status: statusFilter ?? undefined,
      });
      setSubmissions(result.data);
      setTotal(result.total);
    } catch (err: any) {
      setError(err.message ?? "Failed to load submissions.");
    } finally {
      setLoading(false);
    }
  }, [first, rows, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const onPage = (e: DataTablePageEvent) => {
    setFirst(e.first);
    setRows(e.rows);
  };

  const nameTemplate = (row: FundingCampaignSubmission) =>
    `${row.first_name} ${row.last_name}`;

  const statusTemplate = (row: FundingCampaignSubmission) => (
    <Tag value={STATUS_LABEL[row.status]} severity={STATUS_SEVERITY[row.status]} style={{ color: "#fff" }} />
  );

  const dateTemplate = (row: FundingCampaignSubmission) =>
    new Date(row.created_at).toLocaleDateString("de-DE");

  return (
    <div className="surface-section p-4">
      <div className="flex align-items-center gap-2 mb-1">
        <h2 className="m-0">Wallbox-Förderung für Mehrfamilienhäuser 2026</h2>
      </div>
      <p className="text-color-secondary mt-2 mb-4">
        Neue Bundesförderung: 500 Mio. Euro für Wallboxen in Mehrparteienhäusern.
        Bis zu 2.000 Euro pro Stellplatz. Antrag ab 15. April 2026.
      </p>

      {error && <Message severity="error" text={error} className="mb-4 w-full" />}

      <div className="flex flex-wrap gap-2 mb-3">
        <span className="p-input-icon-left">
          <i className="pi pi-search ml-3" />
          <InputText
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, company…"
            className="pl-6"
          />
        </span>
        <Dropdown
          value={statusFilter}
          options={STATUS_OPTIONS}
          onChange={(e) => setStatusFilter(e.value ?? null)}
          placeholder="All statuses"
          showClear
          className="w-12rem"
        />
      </div>

      <DataTable
        value={submissions}
        lazy
        paginator
        rows={rows}
        first={first}
        totalRecords={total}
        onPage={onPage}
        loading={loading}
        rowsPerPageOptions={[10, 25, 50, 100]}
        emptyMessage="No submissions found."
        rowHover
        selectionMode="single"
        onRowClick={(e) => navigate(`/campaigns/wallbox-funding/${(e.data as FundingCampaignSubmission).id}`)}
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
      >
        <Column header="Name" body={nameTemplate} />
        <Column field="email" header="Email" />
        <Column field="company" header="Company" />
        <Column field="city" header="City" />
        <Column field="parking_spaces" header="Parking Spaces" />
        <Column header="Status" body={statusTemplate} />
        <Column header="Submitted" body={dateTemplate} />
      </DataTable>
    </div>
  );
};

export default WallboxFundingPage;
